import React, { useState, useEffect, useMemo } from 'react';
import { Map as LeafletMap, ZoomControl, useLeaflet } from 'react-leaflet';
import { gql, useQuery } from '@apollo/client';
import Immutable from 'immutable';
import { useSearchkitVariables } from '@searchkit/client';
import { usePrevious } from '@uncinc/uncinc-react-kitchen-sink';

import MarkerCluster from './MarkerCluster';
import { mapPadding } from './helpers';
import MapboxLayer from './MapboxLayer';

import './style.scss';

// const url = "https://www.nldoet.nl/api/nldoet/projects?top=52.42266035293937&bottom=52.16859528799938&left=4.7346165776252755&right=5.1191380620002755&type=All&places_available=All&display_full=1";
const GET_PROJECTS = gql`
  query NLDoetSearchresultSet($query: String, $filters: [SKFiltersSet], $page: SKPageInput) {
    nLDoetSearch {
      results(query: $query, filters: $filters) {
        hits(page: $page) {
          page {
            total
            totalPages
            pageNumber
            from
            size
          }
          items {
            ... on NLDoetSearchResultHit {
              id
              distance
              fields {
                title
                field_project_full
                field_highschool_availability
                field_project_type
                field_max_volunteers
                project_type_name
                field_count_own_volunteers
                field_count_volunteers
                address_line1
                field_activity_datetime
                end_value
                locality
                places_available
                places_available_percentage
                latlon
                url
              }
            }
          }
        }
      }
    }
  }
`;

const MapEffect = ({ setBounds }) => {
  const searchkitVariables = useSearchkitVariables();
  const { map } = useLeaflet();

  useEffect(() => {
    if (!map) {
      return;
    }

    searchkitVariables.filters.forEach((filter) => {
      if (filter.identifier === 'fake_box') {
        const b = [
          [filter.geoBoundingBox.bottomRight.lat, filter.geoBoundingBox.topLeft.lon],
          [filter.geoBoundingBox.topLeft.lat, filter.geoBoundingBox.bottomRight.lon],
        ];

        // Reset map bounds and fly to new bounds.
        setBounds(null);
        map.flyToBounds(b, { paddingTopLeft: mapPadding(), duration: 0.5 });
      }
    });
  }, [setBounds, map, searchkitVariables]);

  return null;
};

const Map = ({ width, height, children }) => {
  const PAGE_SIZE = 500;
  const searchkitVariables = useSearchkitVariables();
  const [markers, setMarkers] = useState([]);
  const [bounds, setBounds] = useState([[50.803721015, 3.31497114423], [53.5104033474, 7.09205325687]]);
  const [page, setPage] = useState(0);
  const [mapVisible, setMapVisible] = useState(false);

  // Get active filters and only apply some.
  const filters = useMemo(() => {
    return searchkitVariables.filters.filter((filter) => {
      return [
        'full',
        'highschool_availability',
        'type',
        'date',
        'availability',
      ].includes(filter.identifier);
    });
  }, [searchkitVariables]);

  const previousFilters = usePrevious(filters);

  useEffect(() => {
    if (JSON.stringify(filters) !== JSON.stringify(previousFilters)) {
      // When the searchkit filters change, set page to 0 to do a fresh map query
      setPage(0);
    }
  }, [filters]);

  // We need to fetchPolicy to turn off extra network requests.
  const { loading, error, data, fetchMore } = useQuery(GET_PROJECTS, { fetchPolicy: '"cache-first', variables: { filters: filters, page: { size: PAGE_SIZE, from: page } } });

  useEffect(() => {
    const newMarkers = [];

    if (data && data.nLDoetSearch.results) {
      data.nLDoetSearch.results.hits.items.forEach((item) => {
        if (!item.fields.latlon) {
          return;
        }

        const [lat, lon] = item.fields.latlon[0].split(',');

        const marker = {
          geo_location: {
            lat: lat,
            lon: lon,
          },
          id: item.id,
          ...item.fields,
        };

        newMarkers.push(Immutable.fromJS(marker));
      });

      // Merge markers with previous ones, except if page = 0 which means that the initial markers should be set
      if (page === 0) {
        setMarkers(newMarkers);
      } else {
        setMarkers((currentMarkers) => [...currentMarkers, ...newMarkers]);
      }

      const moreDataAvailable = (data.nLDoetSearch.results.hits.page && data.nLDoetSearch.results.hits.page.pageNumber < data.nLDoetSearch.results.hits.page.totalPages - 1);

      // Check if there are more results available.
      if (moreDataAvailable) {
        fetchMore({
          variables: { page: { size: PAGE_SIZE, from: data.nLDoetSearch.results.hits.page.from + PAGE_SIZE } },
        }).then(() => setPage(data.nLDoetSearch.results.hits.page.from + PAGE_SIZE));
      }
    }
  }, [data, fetchMore, setPage]);

  const toggleMap = () => {
    setMapVisible(!mapVisible);
  };

  if (!width || !height) {
    return null;
  }

  return (
    <div className={`Map ${mapVisible ? 'visible' : ''}`} style={{ height: parseInt(height), width: parseInt(width) }}>
      <LeafletMap
        style={{ height: parseInt(height), width: parseInt(width) }}
        zoomControl={false}
        scrollWheelZoom
        bounds={bounds}
        boundsOptions={{ paddingTopLeft: mapPadding() }}
        maxZoom={22}
        minZoom={7}
      >
        <MapboxLayer
          accessToken={process.env.REACT_APP_MAPBOX_API_KEY}
          /* eslint-disable */
          style="mapbox://styles/oranjefonds/clagrscum003u14o3cd31m9si"
        />
        <ZoomControl position="bottomright" />
        <MarkerCluster markers={markers} />
        <MapEffect setBounds={setBounds} />
        {children}
      </LeafletMap>
      <button type="button" className="Map__toggle" onClick={toggleMap}>
        <i className={`icon icon-before ${mapVisible ? 'icon-close' : 'icon-marker'} icon-variant-4`} />
      </button>
    </div>
  );
};

export default Map;
